import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';

import type { PatientNavigatorItemData } from '@/features/patient-navigator';

import { toPatientNavigatorItems } from '../lib/toPatientNavigatorItems.js';
import { ALL_PATIENT_LIST_LIMIT, DEFAULT_PATIENT_LIST_LIMIT } from './patient-list-limits.js';
import {
  mapPatientListScopeToDesiredRelationship,
  type PatientListScope,
} from './patient-list-scope.js';
import {
  careSearchPatientsQueryDefinition,
  useCareSearchPatientsQuery,
} from './useCareSearchPatientsQuery.js';

interface UseScopedPatientsQueryParams {
  query: string;
  scope: PatientListScope;
}

interface UseScopedPatientsQueryResult {
  patients: PatientNavigatorItemData[];
  isLoading: boolean;
}

/**
 * Dual-query hook that fetches the default (actionable) patient set
 * unconditionally and lazily loads the broader "all" set only when
 * `scope === 'all'`.
 */
export function useScopedPatientsQuery({
  query,
  scope,
}: UseScopedPatientsQueryParams): UseScopedPatientsQueryResult {
  const defaultDesiredRelationship = mapPatientListScopeToDesiredRelationship('default');
  const allDesiredRelationship = mapPatientListScopeToDesiredRelationship('all');

  const defaultPatientsQuery = useCareSearchPatientsQuery({
    query,
    limit: DEFAULT_PATIENT_LIST_LIMIT,
    desired_relationship: defaultDesiredRelationship,
  });

  const allPatientsQuery = useQuery({
    ...careSearchPatientsQueryDefinition({
      query,
      limit: ALL_PATIENT_LIST_LIMIT,
      desired_relationship: allDesiredRelationship,
    }),
    enabled: scope === 'all',
  });

  const patients = useMemo(() => {
    if (scope !== 'all') {
      return toPatientNavigatorItems(defaultPatientsQuery.data?.patients ?? []);
    }
    const raw = allPatientsQuery.data?.patients ?? defaultPatientsQuery.data?.patients ?? [];
    return toPatientNavigatorItems(raw);
  }, [scope, allPatientsQuery.data, defaultPatientsQuery.data]);

  const isLoading =
    scope === 'all'
      ? allPatientsQuery.isLoading || defaultPatientsQuery.isLoading
      : defaultPatientsQuery.isLoading;

  return { patients, isLoading };
}
